import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Trophy,
  Target,
  Activity,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_V2 = `${BACKEND_URL}/api/v2`;

const PerformanceDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      // Use v2 user-specific endpoint when authenticated
      const endpoint = `${API_V2}/user/stats/performance/stats`;
      const response = await axios.get(endpoint, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch performance stats:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 15000); // Refresh every 15s
    return () => clearInterval(interval);
  }, [fetchStats]);

  if (loading || !stats) {
    return (
      <Card className="matrix-card rounded-none">
        <CardContent className="p-8 text-center text-[#008F11]">
          LOADING PERFORMANCE DATA...
        </CardContent>
      </Card>
    );
  }

  // Provide default values for stats to prevent crashes
  const safeStats = {
    total_profit: stats.total_profit || 0,
    win_rate: stats.win_rate || 0,
    winning_trades: stats.winning_trades || 0,
    losing_trades: stats.losing_trades || 0,
    best_trade: stats.best_trade || 0,
    worst_trade: stats.worst_trade || 0,
    total_trades: stats.total_trades || 0,
    avg_profit_per_trade: stats.avg_profit_per_trade || 0,
    profit_by_day: stats.profit_by_day || [],
    profit_by_pair: stats.profit_by_pair || {},
    trades_by_trigger: stats.trades_by_trigger || {},
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="matrix-card p-3 text-sm">
          <p className="text-[#008F11]">{label}</p>
          <p className={payload[0].value >= 0 ? 'text-[#00FF41]' : 'text-red-500'}>
            ${payload[0].value.toFixed(6)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="matrix-card rounded-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase text-[#008F11]">Total Profit</span>
              <TrendingUp className="h-4 w-4 text-[#008F11]" />
            </div>
            <p className={`text-xl font-bold ${safeStats.total_profit >= 0 ? 'text-[#00FF41]' : 'text-red-500'}`}>
              {safeStats.total_profit >= 0 ? '+' : ''}${safeStats.total_profit.toFixed(6)}
            </p>
          </CardContent>
        </Card>

        <Card className="matrix-card rounded-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase text-[#008F11]">Win Rate</span>
              <Trophy className="h-4 w-4 text-[#008F11]" />
            </div>
            <p className="text-xl font-bold text-[#00FF41]">{safeStats.win_rate}%</p>
            <p className="text-xs text-[#008F11]">{safeStats.winning_trades}W / {safeStats.losing_trades}L</p>
          </CardContent>
        </Card>

        <Card className="matrix-card rounded-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase text-[#008F11]">Best Trade</span>
              <TrendingUp className="h-4 w-4 text-[#00FF41]" />
            </div>
            <p className="text-xl font-bold text-[#00FF41]">+${safeStats.best_trade.toFixed(6)}</p>
          </CardContent>
        </Card>

        <Card className="matrix-card rounded-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase text-[#008F11]">Worst Trade</span>
              <TrendingDown className="h-4 w-4 text-red-500" />
            </div>
            <p className="text-xl font-bold text-red-500">${safeStats.worst_trade.toFixed(6)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Profit by Day Chart */}
      {safeStats.profit_by_day && safeStats.profit_by_day.length > 0 && (
        <Card className="matrix-card rounded-none">
          <CardHeader className="border-b border-[#003B00] p-4">
            <CardTitle className="text-sm uppercase tracking-widest flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              DAILY PROFIT
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={safeStats.profit_by_day}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#008F11" 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis 
                    stroke="#008F11" 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => `$${value.toFixed(4)}`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="profit">
                    {safeStats.profit_by_day.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.profit >= 0 ? '#00FF41' : '#FF4444'} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Profit by Pair and Triggers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="matrix-card rounded-none">
          <CardHeader className="border-b border-[#003B00] p-4">
            <CardTitle className="text-sm uppercase tracking-widest flex items-center gap-2">
              <Activity className="h-4 w-4" />
              PROFIT BY PAIR
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {safeStats.profit_by_pair && Object.entries(safeStats.profit_by_pair).length > 0 ? (
              Object.entries(safeStats.profit_by_pair).map(([pair, profit]) => (
                <div key={pair} className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                      pair.startsWith('BTC') ? 'bg-orange-500/20' :
                      pair.startsWith('ETH') ? 'bg-blue-500/20' :
                      'bg-purple-500/20'
                    }`}>
                      {pair.split('-')[0].slice(0, 1)}
                    </div>
                    <span className="text-sm">{pair}</span>
                  </div>
                  <span className={`font-bold ${profit >= 0 ? 'text-[#00FF41]' : 'text-red-500'}`}>
                    {profit >= 0 ? '+' : ''}${(profit || 0).toFixed(6)}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-[#008F11]">NO DATA YET</p>
            )}
          </CardContent>
        </Card>

        <Card className="matrix-card rounded-none">
          <CardHeader className="border-b border-[#003B00] p-4">
            <CardTitle className="text-sm uppercase tracking-widest flex items-center gap-2">
              <Target className="h-4 w-4" />
              TRADES BY TRIGGER
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {safeStats.trades_by_trigger && Object.entries(safeStats.trades_by_trigger).length > 0 ? (
              Object.entries(safeStats.trades_by_trigger).map(([trigger, count]) => (
                <div key={trigger} className="flex justify-between items-center">
                  <Badge className={`rounded-none ${
                    trigger === 'RSI' ? 'bg-blue-900/20 text-blue-400 border-blue-500' :
                    trigger === 'GRID' ? 'bg-cyan-900/20 text-cyan-400 border-cyan-500' :
                    trigger === 'STOP_LOSS' ? 'bg-red-900/20 text-red-400 border-red-500' :
                    trigger === 'TAKE_PROFIT' ? 'bg-green-900/20 text-green-400 border-green-500' :
                    'bg-yellow-900/20 text-yellow-400 border-yellow-500'
                  }`}>
                    {trigger.replace('_', ' ')}
                  </Badge>
                  <span className="font-bold">{count}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-[#008F11]">NO DATA YET</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Summary Stats */}
      <Card className="matrix-card rounded-none">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-xs text-[#008F11] uppercase">Total Trades</p>
              <p className="text-2xl font-bold text-[#00FF41]">{safeStats.total_trades}</p>
            </div>
            <div>
              <p className="text-xs text-[#008F11] uppercase">Avg Profit/Trade</p>
              <p className={`text-2xl font-bold ${safeStats.avg_profit_per_trade >= 0 ? 'text-[#00FF41]' : 'text-red-500'}`}>
                ${safeStats.avg_profit_per_trade.toFixed(6)}
              </p>
            </div>
            <div>
              <p className="text-xs text-[#008F11] uppercase">Winning</p>
              <p className="text-2xl font-bold text-[#00FF41]">{safeStats.winning_trades}</p>
            </div>
            <div>
              <p className="text-xs text-[#008F11] uppercase">Losing</p>
              <p className="text-2xl font-bold text-red-500">{safeStats.losing_trades}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceDashboard;
